import React, { useState, useEffect } from 'react';
import { Search, Download, Trash2, FileText, Eye, X, CreditCard, Edit2, CheckSquare, Square, MoreHorizontal } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function BillingHistory() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/invoices`);
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredInvoices = invoices.filter((invoice) => {
    const matchesSearch =
      invoice.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || invoice.invoice_type === filterType;
    const matchesDateFrom = !dateFrom || invoice.invoice_date >= dateFrom;
    const matchesDateTo = !dateTo || invoice.invoice_date <= dateTo;
    return matchesSearch && matchesType && matchesDateFrom && matchesDateTo;
  });

  const handleDownloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API_URL}/api/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error downloading PDF');
    }
  };

  const handleDelete = async (id, invoiceNumber) => {
    if (!window.confirm(`Are you sure you want to delete invoice ${invoiceNumber}?`)) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/invoices/${id}`);
      setInvoices(invoices.filter((inv) => inv.id !== id));
      setSelectedIds(selectedIds.filter(sid => sid !== id));
    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert('Error deleting invoice');
    }
  };

  // Bulk Actions
  const handleSelectAll = () => {
    if (selectedIds.length === filteredInvoices.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredInvoices.map(inv => inv.id));
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(sid => sid !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      alert('Please select invoices to delete');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} invoices?`)) {
      return;
    }
    try {
      setBulkActionLoading(true);
      await axios.post(`${API_URL}/api/invoices/bulk-delete`, selectedIds);
      setInvoices(invoices.filter(inv => !selectedIds.includes(inv.id)));
      setSelectedIds([]);
      alert(`Successfully deleted ${selectedIds.length} invoices`);
    } catch (error) {
      console.error('Error bulk deleting:', error);
      alert('Error deleting invoices');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Edit Invoice
  const handleEditInvoice = (invoice) => {
    setEditingInvoice({
      ...invoice,
      invoice_number: invoice.invoice_number
    });
  };

  const handleSaveEdit = async () => {
    if (!editingInvoice) return;
    try {
      setBulkActionLoading(true);
      const response = await axios.put(`${API_URL}/api/invoices/${editingInvoice.id}`, editingInvoice);
      setInvoices(invoices.map(inv => inv.id === editingInvoice.id ? response.data : inv));
      setEditingInvoice(null);
      if (selectedInvoice && selectedInvoice.id === editingInvoice.id) {
        setSelectedInvoice(response.data);
      }
      alert('Invoice updated successfully');
    } catch (error) {
      console.error('Error updating invoice:', error);
      alert('Error updating invoice');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div data-testid="history-loading" className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <>
    <div data-testid="billing-history" className="space-y-6 animate-fade-in">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
            <input
              data-testid="search-input"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by invoice number or customer name..."
              className="w-full pl-10 pr-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-sm"
            />
          </div>

          <select
            data-testid="filter-type-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-sm"
          >
            <option value="all">All Types</option>
            <option value="GST">GST</option>
            <option value="Non-GST">Non-GST</option>
          </select>

          <input
            data-testid="date-from-input"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-sm"
          />

          <input
            data-testid="date-to-input"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-sm"
          />
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between">
          <span className="text-sm font-medium text-emerald-800">
            {selectedIds.length} invoice{selectedIds.length > 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-3">
            <button
              data-testid="bulk-delete-btn"
              onClick={handleBulkDelete}
              disabled={bulkActionLoading}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              Delete Selected
            </button>
            <button
              onClick={() => setSelectedIds([])}
              className="px-4 py-2 text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors text-sm"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="px-4 py-3 text-left">
                  <button
                    data-testid="select-all-checkbox"
                    onClick={handleSelectAll}
                    className="p-1 hover:bg-zinc-200 rounded transition-colors"
                  >
                    {selectedIds.length === filteredInvoices.length && filteredInvoices.length > 0 ? (
                      <CheckSquare className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <Square className="w-5 h-5 text-zinc-400" />
                    )}
                  </button>
                </th>
                <th className="px-4 py-3 text-left font-medium text-zinc-700">Invoice No</th>
                <th className="px-4 py-3 text-left font-medium text-zinc-700">Date</th>
                <th className="px-4 py-3 text-left font-medium text-zinc-700">Type</th>
                <th className="px-4 py-3 text-left font-medium text-zinc-700">Customer</th>
                <th className="px-4 py-3 text-left font-medium text-zinc-700">Contact</th>
                <th className="px-4 py-3 text-left font-medium text-zinc-700">EMI</th>
                <th className="px-4 py-3 text-right font-medium text-zinc-700">Amount</th>
                <th className="px-4 py-3 text-center font-medium text-zinc-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-zinc-500">
                    No invoices found
                  </td>
                </tr>
              ) : (
                filteredInvoices.map((invoice) => (
                  <tr 
                    key={invoice.id} 
                    data-testid={`invoice-row-${invoice.id}`} 
                    className={`border-b border-zinc-100 hover:bg-zinc-50 transition-colors cursor-pointer ${selectedIds.includes(invoice.id) ? 'bg-emerald-50' : ''}`}
                    onClick={() => setSelectedInvoice(invoice)}
                  >
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <button
                        data-testid={`select-invoice-${invoice.id}`}
                        onClick={() => handleSelectOne(invoice.id)}
                        className="p-1 hover:bg-zinc-200 rounded transition-colors"
                      >
                        {selectedIds.includes(invoice.id) ? (
                          <CheckSquare className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <Square className="w-5 h-5 text-zinc-400" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 font-medium text-zinc-900">{invoice.invoice_number}</td>
                    <td className="px-4 py-3 text-zinc-600">
                      {new Date(invoice.invoice_date).toLocaleDateString('en-IN')}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                        invoice.invoice_type === 'GST'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-zinc-100 text-zinc-700'
                      }`}>
                        {invoice.invoice_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-zinc-900 max-w-[200px] truncate" title={invoice.customer_name}>{invoice.customer_name}</td>
                    <td className="px-4 py-3 text-zinc-600">{invoice.customer_contact}</td>
                    <td className="px-4 py-3">
                      {invoice.emi_details?.financer_name ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-700">
                          <CreditCard className="w-3 h-3" />
                          EMI
                        </span>
                      ) : (
                        <span className="text-zinc-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-zinc-900 font-[Manrope]">
                      {formatCurrency(invoice.grand_total)}
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1">
                        <button
                          data-testid={`edit-invoice-${invoice.id}`}
                          onClick={() => handleEditInvoice(invoice)}
                          className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                          title="Edit Invoice"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`view-invoice-${invoice.id}`}
                          onClick={() => setSelectedInvoice(invoice)}
                          className="p-2 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`download-pdf-${invoice.id}`}
                          onClick={() => handleDownloadPDF(invoice.id, invoice.invoice_number)}
                          className="p-2 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded transition-colors"
                          title="Download PDF"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`delete-invoice-${invoice.id}`}
                          onClick={() => handleDelete(invoice.id, invoice.invoice_number)}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                          title="Delete Invoice"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filteredInvoices.length > 0 && (
          <div className="px-4 py-3 bg-zinc-50 border-t border-zinc-200 text-sm text-zinc-600">
            Showing {filteredInvoices.length} of {invoices.length} invoices
          </div>
        )}
      </div>
    </div>

      {/* Invoice Detail Modal - Outside animated container */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedInvoice(null)}>
          <div data-testid="invoice-detail-modal" className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-emerald-700 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <h2 className="text-lg font-semibold text-white">
                Invoice {selectedInvoice.invoice_number}
              </h2>
              <button
                data-testid="close-modal-btn"
                onClick={() => setSelectedInvoice(null)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Invoice Info */}
              <div className="bg-zinc-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-zinc-500 uppercase mb-3">Invoice Information</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div><span className="text-zinc-500 block">Invoice No:</span> <span className="font-medium">{selectedInvoice.invoice_number}</span></div>
                  <div><span className="text-zinc-500 block">Type:</span> <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${selectedInvoice.invoice_type === 'GST' ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-200 text-zinc-700'}`}>{selectedInvoice.invoice_type}</span></div>
                  <div><span className="text-zinc-500 block">Date:</span> <span className="font-medium">{new Date(selectedInvoice.invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span></div>
                  <div><span className="text-zinc-500 block">Place of Supply:</span> <span className="font-medium">{selectedInvoice.place_of_supply || '-'}</span></div>
                </div>
              </div>

              {/* Customer Info */}
              <div>
                <h3 className="text-sm font-semibold text-zinc-500 uppercase mb-3">Customer Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div><span className="text-zinc-500">Name:</span> <span className="font-medium break-words">{selectedInvoice.customer_name}</span></div>
                  <div><span className="text-zinc-500">Contact:</span> <span className="font-medium">{selectedInvoice.customer_contact}</span></div>
                  <div className="md:col-span-2"><span className="text-zinc-500">Address:</span> <span className="font-medium break-words">{selectedInvoice.customer_address || '-'}</span></div>
                  {selectedInvoice.customer_gstin && (
                    <div className="md:col-span-2"><span className="text-zinc-500">GSTIN:</span> <span className="font-medium">{selectedInvoice.customer_gstin}</span></div>
                  )}
                </div>
              </div>

              {/* Items */}
              <div>
                <h3 className="text-sm font-semibold text-zinc-500 uppercase mb-3">Items ({selectedInvoice.items?.length || 0})</h3>
                <div className="border border-zinc-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-100">
                      <tr>
                        <th className="px-3 py-2 text-left">#</th>
                        <th className="px-3 py-2 text-left">Item Details</th>
                        <th className="px-3 py-2 text-center">HSN</th>
                        <th className="px-3 py-2 text-right">Qty</th>
                        <th className="px-3 py-2 text-right">Price</th>
                        <th className="px-3 py-2 text-center">GST%</th>
                        <th className="px-3 py-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoice.items?.map((item, idx) => (
                        <tr key={idx} className="border-t border-zinc-100">
                          <td className="px-3 py-2 text-zinc-500">{idx + 1}</td>
                          <td className="px-3 py-2">
                            <div className="font-medium break-words">{item.item_name}</div>
                            {item.description && <div className="text-xs text-zinc-500 break-words">{item.description}</div>}
                            {item.serial_number && <div className="text-xs text-blue-600">S/N: {item.serial_number}</div>}
                            {item.imei1 && <div className="text-xs text-blue-600">IMEI1: {item.imei1}</div>}
                            {item.imei2 && <div className="text-xs text-blue-600">IMEI2: {item.imei2}</div>}
                            {item.mrp && <div className="text-xs text-zinc-500">MRP: {formatCurrency(item.mrp)}</div>}
                          </td>
                          <td className="px-3 py-2 text-center text-zinc-600">{item.hsn_sac_code || '-'}</td>
                          <td className="px-3 py-2 text-right">{item.quantity} {item.unit}</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(item.sale_price)}</td>
                          <td className="px-3 py-2 text-center">{item.gst_percentage}%</td>
                          <td className="px-3 py-2 text-right font-medium">{formatCurrency(item.total_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* GST Breakdown */}
              {selectedInvoice.invoice_type === 'GST' && selectedInvoice.items?.[0] && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-blue-700 uppercase mb-3">GST Breakdown</h3>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div><span className="text-zinc-500 block">Base Price:</span> <span className="font-medium">{formatCurrency(selectedInvoice.subtotal)}</span></div>
                    <div><span className="text-zinc-500 block">CGST:</span> <span className="font-medium">{formatCurrency(selectedInvoice.total_gst / 2)}</span></div>
                    <div><span className="text-zinc-500 block">SGST:</span> <span className="font-medium">{formatCurrency(selectedInvoice.total_gst / 2)}</span></div>
                  </div>
                </div>
              )}

              {/* EMI Details */}
              {selectedInvoice.emi_details?.financer_name && (
                <div>
                  <h3 className="text-sm font-semibold text-zinc-500 uppercase mb-3">EMI / Finance Details</h3>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      {selectedInvoice.emi_details.financer_name && (
                        <div><span className="text-zinc-500 block">Financer:</span> <span className="font-medium break-words">{selectedInvoice.emi_details.financer_name}</span></div>
                      )}
                      {selectedInvoice.emi_details.finance_scheme && (
                        <div><span className="text-zinc-500 block">Scheme:</span> <span className="font-medium break-words">{selectedInvoice.emi_details.finance_scheme}</span></div>
                      )}
                      {selectedInvoice.emi_details.price && (
                        <div><span className="text-zinc-500 block">Finance Price:</span> <span className="font-medium">{formatCurrency(selectedInvoice.emi_details.price)}</span></div>
                      )}
                      {selectedInvoice.emi_details.down_payment && (
                        <div><span className="text-zinc-500 block">Down Payment:</span> <span className="font-medium">{formatCurrency(selectedInvoice.emi_details.down_payment)}</span></div>
                      )}
                      {selectedInvoice.emi_details.emi_months && (
                        <div><span className="text-zinc-500 block">EMI Months:</span> <span className="font-medium">{selectedInvoice.emi_details.emi_months}</span></div>
                      )}
                      {selectedInvoice.emi_details.emi_amount && (
                        <div><span className="text-zinc-500 block">EMI Amount:</span> <span className="font-medium">{formatCurrency(selectedInvoice.emi_details.emi_amount)}</span></div>
                      )}
                      {selectedInvoice.emi_details.first_month_emi_amount && (
                        <div><span className="text-zinc-500 block">1st Month EMI:</span> <span className="font-medium">{formatCurrency(selectedInvoice.emi_details.first_month_emi_amount)}</span></div>
                      )}
                      {selectedInvoice.emi_details.first_emi_start_date && (
                        <div><span className="text-zinc-500 block">1st EMI Date:</span> <span className="font-medium">{selectedInvoice.emi_details.first_emi_start_date}</span></div>
                      )}
                      {selectedInvoice.emi_details.emi_account_details && (
                        <div className="md:col-span-3"><span className="text-zinc-500 block">Account Details:</span> <span className="font-medium break-words">{selectedInvoice.emi_details.emi_account_details}</span></div>
                      )}
                      {selectedInvoice.emi_details.product_insurance_details && (
                        <div className="md:col-span-3"><span className="text-zinc-500 block">Insurance:</span> <span className="font-medium break-words">{selectedInvoice.emi_details.product_insurance_details}</span></div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Custom Fields */}
              {selectedInvoice.custom_fields && Object.keys(selectedInvoice.custom_fields).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-zinc-500 uppercase mb-3">Additional Fields</h3>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {Object.entries(selectedInvoice.custom_fields).map(([key, value]) => (
                        <div key={key}><span className="text-zinc-500 block">{key}:</span> <span className="font-medium break-words">{value}</span></div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Totals */}
              <div className="bg-zinc-100 rounded-lg p-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-600">Subtotal:</span>
                    <span className="font-medium">{formatCurrency(selectedInvoice.subtotal)}</span>
                  </div>
                  {selectedInvoice.invoice_type === 'GST' && (
                    <div className="flex justify-between">
                      <span className="text-zinc-600">Total GST (CGST + SGST):</span>
                      <span className="font-medium">{formatCurrency(selectedInvoice.total_gst)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-zinc-300 text-emerald-700">
                    <span>Grand Total:</span>
                    <span>{formatCurrency(selectedInvoice.grand_total)}</span>
                  </div>
                  <div className="text-xs text-zinc-500 italic pt-1">
                    {selectedInvoice.amount_in_words}
                  </div>
                </div>
              </div>

              {/* Created Date */}
              {selectedInvoice.created_at && (
                <div className="text-xs text-zinc-400 text-center">
                  Created on: {new Date(selectedInvoice.created_at).toLocaleString('en-IN')}
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-zinc-200 flex gap-3">
              <button
                onClick={() => handleDownloadPDF(selectedInvoice.id, selectedInvoice.invoice_number)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition-colors font-medium"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </button>
              <button
                onClick={() => {
                  handleEditInvoice(selectedInvoice);
                  setSelectedInvoice(null);
                }}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <Edit2 className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={() => setSelectedInvoice(null)}
                className="px-6 py-2 bg-zinc-200 text-zinc-700 rounded-lg hover:bg-zinc-300 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Invoice Modal */}
      {editingInvoice && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditingInvoice(null)}>
          <div data-testid="edit-invoice-modal" className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-blue-600 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <h2 className="text-lg font-semibold text-white">
                Edit Invoice
              </h2>
              <button
                data-testid="close-edit-modal-btn"
                onClick={() => setEditingInvoice(null)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Invoice Number (Editable) */}
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">Invoice Number *</label>
                <input
                  data-testid="edit-invoice-number"
                  type="text"
                  value={editingInvoice.invoice_number}
                  onChange={(e) => setEditingInvoice({...editingInvoice, invoice_number: e.target.value})}
                  className="w-full px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="Enter invoice number"
                />
              </div>

              {/* Invoice Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">Invoice Type</label>
                  <select
                    value={editingInvoice.invoice_type}
                    onChange={(e) => setEditingInvoice({...editingInvoice, invoice_type: e.target.value})}
                    className="w-full px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  >
                    <option value="GST">GST</option>
                    <option value="Non-GST">Non-GST</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">Invoice Date</label>
                  <input
                    type="date"
                    value={editingInvoice.invoice_date}
                    onChange={(e) => setEditingInvoice({...editingInvoice, invoice_date: e.target.value})}
                    className="w-full px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                </div>
              </div>

              {/* Customer Details */}
              <div className="bg-zinc-50 rounded-lg p-4 space-y-4">
                <h3 className="text-sm font-semibold text-zinc-500 uppercase">Customer Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-1">Customer Name</label>
                    <input
                      type="text"
                      value={editingInvoice.customer_name}
                      onChange={(e) => setEditingInvoice({...editingInvoice, customer_name: e.target.value})}
                      className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-1">Contact</label>
                    <input
                      type="text"
                      value={editingInvoice.customer_contact}
                      onChange={(e) => setEditingInvoice({...editingInvoice, customer_contact: e.target.value})}
                      className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Address</label>
                  <textarea
                    value={editingInvoice.customer_address || ''}
                    onChange={(e) => setEditingInvoice({...editingInvoice, customer_address: e.target.value})}
                    rows={2}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">GSTIN (Optional)</label>
                  <input
                    type="text"
                    value={editingInvoice.customer_gstin || ''}
                    onChange={(e) => setEditingInvoice({...editingInvoice, customer_gstin: e.target.value})}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                  />
                </div>
              </div>

              {/* Amounts (Read-only info) */}
              <div className="bg-emerald-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-emerald-700 uppercase mb-3">Invoice Summary</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-zinc-500 block">Subtotal</span>
                    <span className="font-semibold">{formatCurrency(editingInvoice.subtotal)}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500 block">GST</span>
                    <span className="font-semibold">{formatCurrency(editingInvoice.total_gst)}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500 block">Grand Total</span>
                    <span className="font-bold text-emerald-700">{formatCurrency(editingInvoice.grand_total)}</span>
                  </div>
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  * To edit amounts, please create a new invoice with the correct details.
                </p>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-zinc-200 flex gap-3">
              <button
                data-testid="save-edit-btn"
                onClick={handleSaveEdit}
                disabled={bulkActionLoading || !editingInvoice.invoice_number}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
              >
                {bulkActionLoading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={() => setEditingInvoice(null)}
                className="px-6 py-2 bg-zinc-200 text-zinc-700 rounded-lg hover:bg-zinc-300 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default BillingHistory;
